import React, { useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyRoundedIcon from '@mui/icons-material/KeyRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined'
import { ArrowsClockwise, CopySimple, DownloadSimple, PencilSimpleLine, ShieldCheck } from '@phosphor-icons/react'
import GuidanceNote from '../../../components/GuidanceNote'
import LoadingSpinner from '../../../components/LoadingSpinner'
import FormHeaderDetailActions from '../../../components/action_button/FormHeaderDetailActions'
import FormHeaderSaveActions from '../../../components/action_button/FormHeaderSaveActions'
import {
  UI_COLOR_BORDER_LIGHT,
  UI_COLOR_PRIMARY,
  UI_COLOR_TEXT_MUTED_LIGHT,
  UI_COLOR_TEXT_SUBTITLE,
  UI_COLOR_WHITE
} from '../../../constants/uiColors'
import {
  confirmMfa,
  disableMfa,
  fetchMyProfile,
  fetchSecurityStatus,
  regenerateBackupCodes,
  requestMyPasswordChangeCode,
  resetMyPasswordWithToken,
  reconfigureMfa,
  setupMfa,
  verifyMyPasswordResetCode
} from '../services/profileApi'
import { setMfaEnabledCookie, setSecurityConfiguredCookie } from '../../../auth/sessionCookies'
import ProfileSecurityFeatureCard from './components/ProfileSecurityFeatureCard'
import ProfileSecurityModalShell from './components/ProfileSecurityModalShell'
import ProfileSecurityPasswordRequirement from './components/ProfileSecurityPasswordRequirement'
import ProfileSecurityRequirementChip from './components/ProfileSecurityRequirementChip'
import ProfileSecurityStatusChip from './components/ProfileSecurityStatusChip'
import ProfileSecurityStyledField from './components/ProfileSecurityStyledField'
import ProfileSecurityVerificationCodeInputs from './components/ProfileSecurityVerificationCodeInputs'
import {
  PROFILE_SECURITY_BACKUP_CODE_ICON_COLOR,
  PROFILE_SECURITY_BACKUP_HINT_ICON_COLOR,
  PROFILE_SECURITY_COPY,
  PROFILE_SECURITY_DEFAULT_EXPIRES_MINUTES,
  PROFILE_SECURITY_GUIDANCE_NOTE_PROPS,
  PROFILE_SECURITY_HEADING_COLOR,
  PROFILE_SECURITY_LOADING_BOX_SX,
  PROFILE_SECURITY_MODAL_GUIDANCE_PROPS,
  PROFILE_SECURITY_WARNING_NOTE_PROPS
} from './constants/profileSecurityConstants'
import {
  authenticatorCardDescription,
  buildEmptyPasswordForm,
  buildEmptyReconfigureForm,
  buildHandleCodeChange,
  buildHandleCodeKeyDown,
  buildHandleCodePaste,
  buildPasswordChecks,
  buildPasswordRequirements,
  buildSecurityStatusItems,
  buildVerificationDigits,
  canSubmitVerificationCode,
  downloadBackupCodesFile,
  emailCardDescription,
  focusCodeInput,
  getExpiresInMinutes,
  getReconfigureMfaErrorMessage,
  passwordChangeErrorMessage,
  recoveryCardDescription
} from './helpers/profileSecurityHelpers'

export default function ProfileSecuritySection({ onActionAlert }) {
  const [security, setSecurity] = useState(null)
  const [loadingSecurity, setLoadingSecurity] = useState(true)
  const [profileEmail, setProfileEmail] = useState('')
  const [passwordForm, setPasswordForm] = useState(buildEmptyPasswordForm)
  const [passwordStep, setPasswordStep] = useState('verify')
  const [passwordCode, setPasswordCode] = useState('')
  const [passwordCodeSent, setPasswordCodeSent] = useState(false)
  const [passwordCodeExpiresMinutes, setPasswordCodeExpiresMinutes] = useState(PROFILE_SECURITY_DEFAULT_EXPIRES_MINUTES)
  const [passwordResetToken, setPasswordResetToken] = useState('')
  const [setupData, setSetupData] = useState(null)
  const [confirmCode, setConfirmCode] = useState('')
  const [reconfigureForm, setReconfigureForm] = useState(buildEmptyReconfigureForm)
  const [regenerateForm, setRegenerateForm] = useState(buildEmptyReconfigureForm)
  const [backupCodes, setBackupCodes] = useState([])
  const [busy, setBusy] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    passwordConfirmation: false
  })
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false)
  const [mfaReconfigureOpen, setMfaReconfigureOpen] = useState(false)
  const [mfaDisableOpen, setMfaDisableOpen] = useState(false)
  const [disableForm, setDisableForm] = useState(buildEmptyReconfigureForm)
  const [mfaReconfiguring, setMfaReconfiguring] = useState(false)
  const [regenerateOpen, setRegenerateOpen] = useState(false)
  const [backupOpen, setBackupOpen] = useState(false)
  const passwordCodeInputRefs = useRef([])
  const mfaCodeInputRefs = useRef([])

  const loadSecurity = () => {
    setLoadingSecurity(true)
    Promise.all([fetchSecurityStatus(), fetchMyProfile()])
      .then(([securityData, profileData]) => {
        setSecurity(securityData)
        setProfileEmail(profileData?.email || '')
        if (securityData?.mfa_enabled !== undefined) {
          setMfaEnabledCookie(Boolean(securityData.mfa_enabled))
        }
        if (securityData?.security_configured !== undefined) {
          setSecurityConfiguredCookie(Boolean(securityData.security_configured))
        }
      })
      .catch(() => onActionAlert?.(PROFILE_SECURITY_COPY.loadError, 'warning'))
      .finally(() => setLoadingSecurity(false))
  }

  useEffect(() => {
    loadSecurity()
  }, [])

  const showMessage = (type, text) => {
    onActionAlert?.(text, type === 'error' ? 'warning' : 'success')
  }

  const resetPasswordFlow = () => {
    setPasswordStep('verify')
    setPasswordCode('')
    setPasswordCodeSent(false)
    setPasswordCodeExpiresMinutes(PROFILE_SECURITY_DEFAULT_EXPIRES_MINUTES)
    setPasswordResetToken('')
    setPasswordForm(buildEmptyPasswordForm())
    setPasswordVisibility({
      password: false,
      passwordConfirmation: false
    })
  }

  const mfaEnabled = Boolean(security?.mfa_enabled)
  const emailMfaEnabled = Boolean(security?.email_mfa_enabled || security?.email_verified)
  const securityConfigured = Boolean(security?.security_configured || emailMfaEnabled || mfaEnabled)
  const backupRemaining = security?.mfa_backup_codes_remaining || 0
  const mfaTrustDeviceDays = security?.mfa_trust_device_days ?? 1
  const hasFreshBackupCodes = backupCodes.length > 0
  const securityStatusItems = buildSecurityStatusItems({
    emailActive: emailMfaEnabled,
    mfaEnabled,
    backupRemaining,
    hasFreshBackupCodes
  })
  const passwordVerificationDigits = buildVerificationDigits(passwordCode)
  const canSubmitPasswordVerification = canSubmitVerificationCode(passwordCode)
  const mfaVerificationDigits = buildVerificationDigits(confirmCode)
  const canSubmitMfaVerification = canSubmitVerificationCode(confirmCode)

  const passwordChecks = useMemo(
    () => buildPasswordChecks(passwordForm.password, passwordForm.passwordConfirmation),
    [passwordForm.password, passwordForm.passwordConfirmation]
  )

  const passwordRequirements = buildPasswordRequirements(passwordChecks)

  const hasStartedPassword = passwordForm.password.length > 0
  const hasStartedConfirmation = passwordForm.passwordConfirmation.length > 0
  const passwordIsValid = passwordRequirements.every((requirement) => requirement.isMet)

  const getPasswordFieldType = (field) => (passwordVisibility[field] ? 'text' : 'password')

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((current) => ({ ...current, [field]: !current[field] }))
  }

  const handlePasswordCodeChange = buildHandleCodeChange(
    passwordVerificationDigits,
    setPasswordCode,
    passwordCodeInputRefs
  )
  const handlePasswordCodeKeyDown = buildHandleCodeKeyDown(
    passwordVerificationDigits,
    setPasswordCode,
    passwordCodeInputRefs
  )
  const handlePasswordCodePaste = buildHandleCodePaste(setPasswordCode, passwordCodeInputRefs)

  const handleMfaCodeChange = buildHandleCodeChange(
    mfaVerificationDigits,
    setConfirmCode,
    mfaCodeInputRefs
  )
  const handleMfaCodeKeyDown = buildHandleCodeKeyDown(
    mfaVerificationDigits,
    setConfirmCode,
    mfaCodeInputRefs
  )
  const handleMfaCodePaste = buildHandleCodePaste(setConfirmCode, mfaCodeInputRefs)

  const getPasswordAdornment = (field) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          edge="end"
          aria-label={passwordVisibility[field] ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          onClick={() => togglePasswordVisibility(field)}
        >
          {passwordVisibility[field] ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
        </IconButton>
      </InputAdornment>
    )
  })

  const closePasswordModal = () => {
    setPasswordOpen(false)
    resetPasswordFlow()
  }

  const openPasswordModal = () => {
    resetPasswordFlow()
    setPasswordOpen(true)
  }

  const closeMfaSetupModal = () => {
    if (busy) return
    setMfaSetupOpen(false)
    setSetupData(null)
    setConfirmCode('')
    setMfaReconfiguring(false)
  }

  const closeMfaReconfigureModal = () => {
    if (busy) return
    setMfaReconfigureOpen(false)
    setReconfigureForm(buildEmptyReconfigureForm())
  }

  const closeMfaDisableModal = () => {
    if (busy) return
    setMfaDisableOpen(false)
    setDisableForm(buildEmptyReconfigureForm())
  }

  const closeRegenerateModal = () => {
    if (busy) return
    setRegenerateOpen(false)
    setRegenerateForm(buildEmptyReconfigureForm())
  }

  const closeBackupModal = () => {
    setBackupOpen(false)
    setBackupCodes([])
  }

  const handleRequestPasswordCode = () => {
    setBusy(true)
    requestMyPasswordChangeCode()
      .then((data) => {
        setPasswordCode('')
        setPasswordCodeSent(true)
        setPasswordCodeExpiresMinutes(getExpiresInMinutes(data?.expires_in_minutes))
        showMessage('success', PROFILE_SECURITY_COPY.passwordCodeSent)
        window.requestAnimationFrame(() => {
          focusCodeInput(passwordCodeInputRefs, 0)
        })
      })
      .catch((error) => showMessage('error', passwordChangeErrorMessage(error)))
      .finally(() => setBusy(false))
  }

  const handleVerifyPasswordCode = (event) => {
    event?.preventDefault()
    if (!profileEmail) {
      showMessage('error', PROFILE_SECURITY_COPY.missingEmail)
      return
    }

    if (!canSubmitPasswordVerification) {
      showMessage('error', PROFILE_SECURITY_COPY.invalidVerificationCode)
      return
    }

    setBusy(true)
    verifyMyPasswordResetCode({
      email: profileEmail.trim(),
      code: passwordCode
    })
      .then((data) => {
        setPasswordResetToken(data?.reset_password_token || '')
        setPasswordStep('password')
        setPasswordForm(buildEmptyPasswordForm())
        showMessage('success', PROFILE_SECURITY_COPY.passwordCodeVerified)
      })
      .catch((error) => showMessage('error', passwordChangeErrorMessage(error)))
      .finally(() => setBusy(false))
  }

  const handlePasswordChange = (event) => {
    event.preventDefault()
    if (passwordForm.password !== passwordForm.passwordConfirmation) {
      showMessage('error', PROFILE_SECURITY_COPY.passwordMismatch)
      return
    }

    if (!passwordIsValid) {
      showMessage('error', PROFILE_SECURITY_COPY.passwordRulesInvalid)
      return
    }

    setBusy(true)
    resetMyPasswordWithToken({
      resetPasswordToken: passwordResetToken,
      password: passwordForm.password,
      passwordConfirmation: passwordForm.passwordConfirmation
    })
      .then(() => {
        closePasswordModal()
        showMessage('success', PROFILE_SECURITY_COPY.passwordChanged)
      })
      .catch((error) => showMessage('error', passwordChangeErrorMessage(error)))
      .finally(() => setBusy(false))
  }

  const handleSetupMfa = () => {
    setBusy(true)
    setupMfa()
      .then((data) => {
        setSetupData(data)
        setSecurity(data)
        setBackupCodes([])
        setMfaSetupOpen(true)
        setMfaReconfiguring(false)
      })
      .catch((error) => showMessage('error', error.message || PROFILE_SECURITY_COPY.setupMfaError))
      .finally(() => setBusy(false))
  }

  const handleConfirmMfa = () => {
    setBusy(true)
    confirmMfa(confirmCode.trim())
      .then((data) => {
        setSecurity(data)
        setSetupData(null)
        setConfirmCode('')
        setMfaSetupOpen(false)
        setMfaReconfiguring(false)
        setBackupCodes(data.backup_codes || [])
        setBackupOpen(true)
        setMfaEnabledCookie(true)
        setSecurityConfiguredCookie(true)
        showMessage(
          'success',
          mfaReconfiguring ? PROFILE_SECURITY_COPY.mfaReconfigured : PROFILE_SECURITY_COPY.mfaEnabled
        )
      })
      .catch((error) => showMessage('error', error.message || PROFILE_SECURITY_COPY.mfaInvalidCode))
      .finally(() => setBusy(false))
  }

  const handleReconfigureMfa = () => {
    if (!reconfigureForm.currentPassword || !reconfigureForm.code.trim()) {
      showMessage('error', PROFILE_SECURITY_COPY.reconfigureValidation)
      return
    }

    setBusy(true)
    reconfigureMfa(reconfigureForm)
      .then((data) => {
        setSecurity(data)
        setSetupData(data)
        setConfirmCode('')
        setReconfigureForm(buildEmptyReconfigureForm())
        setMfaReconfigureOpen(false)
        setMfaReconfiguring(true)
        setMfaSetupOpen(true)
      })
      .catch((error) => showMessage('error', getReconfigureMfaErrorMessage(error)))
      .finally(() => setBusy(false))
  }

  const handleDisableMfa = () => {
    if (!disableForm.currentPassword || !disableForm.code.trim()) {
      showMessage('error', PROFILE_SECURITY_COPY.reconfigureValidation)
      return
    }

    setBusy(true)
    disableMfa(disableForm)
      .then((data) => {
        setSecurity(data)
        setDisableForm(buildEmptyReconfigureForm())
        setMfaDisableOpen(false)
        setMfaEnabledCookie(false)
        if (data?.security_configured !== undefined) {
          setSecurityConfiguredCookie(Boolean(data.security_configured))
        }
        showMessage('success', PROFILE_SECURITY_COPY.mfaDisabled)
      })
      .catch((error) => showMessage('error', getReconfigureMfaErrorMessage(error)))
      .finally(() => setBusy(false))
  }

  const handleRegenerateBackupCodes = () => {
    setBusy(true)
    regenerateBackupCodes(regenerateForm)
      .then((data) => {
        setSecurity(data)
        setRegenerateForm(buildEmptyReconfigureForm())
        setRegenerateOpen(false)
        setBackupCodes(data.backup_codes || [])
        setBackupOpen(true)
        showMessage('success', PROFILE_SECURITY_COPY.backupCodesGenerated)
      })
      .catch((error) =>
        showMessage('error', error.message || PROFILE_SECURITY_COPY.backupCodesRegenerateError)
      )
      .finally(() => setBusy(false))
  }

  const handleCopyBackupCodes = async () => {
    if (!backupCodes.length) return
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'))
      closeBackupModal()
      showMessage('success', PROFILE_SECURITY_COPY.backupCodesCopied)
    } catch (_error) {
      showMessage('error', PROFILE_SECURITY_COPY.backupCodesCopyError)
    }
  }

  const handleDownloadBackupCodes = () => {
    if (!backupCodes.length) return
    downloadBackupCodesFile(backupCodes)
    showMessage('success', PROFILE_SECURITY_COPY.backupCodesDownloaded)
  }

  return (
    <Box>
      {loadingSecurity ? (
        <Box sx={PROFILE_SECURITY_LOADING_BOX_SX}>
          <LoadingSpinner text={PROFILE_SECURITY_COPY.loading} />
        </Box>
      ) : (
        <>
          {!securityConfigured && (
            <GuidanceNote
              icon={<InfoOutlinedIcon />}
              title="Verificación pendiente"
              message="Necesita al menos un método de verificación para usar los módulos. El principal es un código que llega a su correo."
              {...PROFILE_SECURITY_GUIDANCE_NOTE_PROPS}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2.5 },
              mb: 2,
              px: 0.25
            }}
          >
            {securityStatusItems.map((item, index) => (
              <React.Fragment key={item.key}>
                {index > 0 ? (
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      width: '1px',
                      height: 18,
                      bgcolor: UI_COLOR_BORDER_LIGHT
                    }}
                  />
                ) : null}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: '0.09em',
                      color: UI_COLOR_TEXT_SUBTITLE,
                      textTransform: 'uppercase'
                    }}
                  >
                    {item.label}
                  </Typography>
                  <ProfileSecurityStatusChip label={item.value} tone={item.tone} />
                </Box>
              </React.Fragment>
            ))}
          </Box>

          <Stack spacing={1.75}>
            <ProfileSecurityFeatureCard
              eyebrow={PROFILE_SECURITY_COPY.passwordCardEyebrow}
              title={PROFILE_SECURITY_COPY.passwordCardTitle}
              description={PROFILE_SECURITY_COPY.passwordCardDescription}
              icon={LockOutlinedIcon}
              action={
                <FormHeaderDetailActions
                  showSecondary={false}
                  primaryLabel={PROFILE_SECURITY_COPY.passwordCardAction}
                  primaryIcon={PencilSimpleLine}
                  onPrimary={openPasswordModal}
                  primaryDisabled={!securityConfigured}
                />
              }
            />

            <ProfileSecurityFeatureCard
              eyebrow={PROFILE_SECURITY_COPY.emailCardEyebrow}
              title={PROFILE_SECURITY_COPY.emailCardTitle}
              description={emailCardDescription({ emailMfaEnabled, profileEmail })}
              icon={EmailOutlinedIcon}
              iconTone={emailMfaEnabled ? 'success' : 'warning'}
            />

            <ProfileSecurityFeatureCard
              eyebrow={
                mfaEnabled
                  ? PROFILE_SECURITY_COPY.authenticatorCardEyebrowActive
                  : PROFILE_SECURITY_COPY.authenticatorCardEyebrowOptional
              }
              title={PROFILE_SECURITY_COPY.authenticatorCardTitle}
              description={authenticatorCardDescription({ mfaEnabled, mfaTrustDeviceDays })}
              icon={ShieldOutlinedIcon}
              iconTone={mfaEnabled ? 'success' : 'primary'}
              action={
                mfaEnabled ? (
                  <FormHeaderDetailActions
                    showSecondary={emailMfaEnabled}
                    secondaryLabel={PROFILE_SECURITY_COPY.authenticatorCardActionDisable}
                    secondaryVariant="danger"
                    showSecondaryIcon={false}
                    primaryLabel={PROFILE_SECURITY_COPY.authenticatorCardActionReconfigure}
                    primaryIcon={ArrowsClockwise}
                    primaryButtonClassName="profile-security-card-action--compact"
                    onSecondary={() => setMfaDisableOpen(true)}
                    onPrimary={() => setMfaReconfigureOpen(true)}
                    secondaryDisabled={busy}
                    primaryDisabled={busy}
                  />
                ) : (
                  <FormHeaderDetailActions
                    showSecondary={false}
                    primaryLabel={PROFILE_SECURITY_COPY.authenticatorCardActionActivate}
                    primaryIconNode={<ShieldCheck size={16} weight="bold" />}
                    primaryButtonClassName="profile-security-card-action--compact"
                    onPrimary={handleSetupMfa}
                    primaryDisabled={busy}
                  />
                )
              }
            />

            <ProfileSecurityFeatureCard
              eyebrow={PROFILE_SECURITY_COPY.recoveryCardEyebrow}
              title={PROFILE_SECURITY_COPY.recoveryCardTitle}
              description={recoveryCardDescription({
                mfaEnabled,
                backupRemaining,
                hasFreshBackupCodes
              })}
              icon={VpnKeyOutlinedIcon}
              iconTone={hasFreshBackupCodes ? 'warning' : mfaEnabled ? 'success' : 'primary'}
              action={
                mfaEnabled ? (
                  <FormHeaderDetailActions
                    showSecondary={false}
                    primaryLabel={PROFILE_SECURITY_COPY.recoveryCardAction}
                    primaryIcon={ArrowsClockwise}
                    onPrimary={() => setRegenerateOpen(true)}
                    primaryDisabled={busy}
                  />
                ) : null
              }
            />
          </Stack>
        </>
      )}

      <ProfileSecurityModalShell
        open={passwordOpen}
        onClose={closePasswordModal}
        title={passwordStep === 'verify' ? 'Verificar identidad' : PROFILE_SECURITY_COPY.passwordCardTitle}
        subtitle={
          passwordStep === 'verify'
            ? `Ingrese el código enviado a ${profileEmail || 'su correo'}${
                mfaEnabled ? ' o el de su app autenticadora' : ''
              }.`
            : 'Cree una contraseña nueva, segura y fácil de recordar.'
        }
        actions={
          <FormHeaderSaveActions
            onCancel={closePasswordModal}
            onPrimaryClick={passwordStep === 'verify' ? handleVerifyPasswordCode : handlePasswordChange}
            primaryButtonType="button"
            className="profile-security-modal-actions"
            primaryLabel={passwordStep === 'verify' ? 'Verificar' : 'Actualizar'}
            savingLabel={passwordStep === 'verify' ? 'Verificando...' : 'Guardando...'}
            isSaving={busy}
            showPrimaryIcon={false}
            primaryDisabled={
              passwordStep === 'verify'
                ? busy || !canSubmitPasswordVerification
                : busy || !passwordResetToken || !passwordIsValid || !passwordChecks.match
            }
          />
        }
      >
        {passwordStep === 'verify' ? (
          <Box component="form" onSubmit={handleVerifyPasswordCode}>
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '14px',
                  border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
                  backgroundColor: '#FAFBFC'
                }}
              >
                <Typography sx={{ fontSize: 13.5, color: UI_COLOR_TEXT_SUBTITLE, mb: 1.5 }}>
                  Use el código de 6 dígitos que le enviamos al correo
                  {mfaEnabled ? ' o el de su app autenticadora' : ''}.
                  {passwordCodeSent ? ` Vence en ${passwordCodeExpiresMinutes} minutos.` : ''}
                </Typography>
                <ProfileSecurityVerificationCodeInputs
                  digits={passwordVerificationDigits}
                  inputRefs={passwordCodeInputRefs}
                  onChange={handlePasswordCodeChange}
                  onKeyDown={handlePasswordCodeKeyDown}
                  onPaste={handlePasswordCodePaste}
                  ariaLabelPrefix="Dígito del código de verificación"
                  idPrefix="password-code"
                  focusColor={UI_COLOR_PRIMARY}
                />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <button
                  type="button"
                  className="change-password-link login-link-button"
                  onClick={handleRequestPasswordCode}
                  disabled={busy}
                  style={{ alignSelf: 'flex-start', fontSize: 13, fontWeight: 600 }}
                >
                  {passwordCodeSent ? 'Reenviar código al correo' : 'Enviar código al correo'}
                </button>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box component="form" onSubmit={handlePasswordChange}>
            <Stack spacing={1.75}>
              <ProfileSecurityStyledField
                label="Nueva contraseña"
                type={getPasswordFieldType('password')}
                value={passwordForm.password}
                onChange={(event) =>
                  setPasswordForm((current) => ({ ...current, password: event.target.value.slice(0, 15) }))
                }
                required
                autoComplete="new-password"
                InputProps={getPasswordAdornment('password')}
              />
              <ProfileSecurityStyledField
                label="Confirmar contraseña"
                type={getPasswordFieldType('passwordConfirmation')}
                value={passwordForm.passwordConfirmation}
                onChange={(event) =>
                  setPasswordForm((current) => ({
                    ...current,
                    passwordConfirmation: event.target.value.slice(0, 15)
                  }))
                }
                required
                autoComplete="new-password"
                InputProps={getPasswordAdornment('passwordConfirmation')}
              />
              {hasStartedPassword ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <ProfileSecurityRequirementChip isMet={passwordChecks.length} label="8 a 15 caracteres" />
                  {hasStartedConfirmation ? (
                    <ProfileSecurityRequirementChip isMet={passwordChecks.match} label="Coinciden" />
                  ) : null}
                </Stack>
              ) : null}
              {hasStartedPassword ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '14px',
                    border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
                    backgroundColor: '#FAFBFC'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: UI_COLOR_TEXT_SUBTITLE,
                      mb: 1.25
                    }}
                  >
                    Reglas de la contraseña
                  </Typography>
                  <Stack spacing={0.9}>
                    {passwordRequirements.map((requirement) => (
                      <ProfileSecurityPasswordRequirement
                        key={requirement.key}
                        isMet={requirement.isMet}
                        label={requirement.label}
                      />
                    ))}
                    {hasStartedConfirmation ? (
                      <ProfileSecurityPasswordRequirement
                        isMet={passwordChecks.match}
                        label="Las contraseñas coinciden"
                      />
                    ) : null}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          </Box>
        )}
      </ProfileSecurityModalShell>

      <ProfileSecurityModalShell
        open={mfaSetupOpen}
        onClose={closeMfaSetupModal}
        title={mfaReconfiguring ? 'Reconfigurar autenticación en dos pasos' : PROFILE_SECURITY_COPY.mfaSetupTitle}
        subtitle={
          mfaReconfiguring
            ? 'Escanee el QR en el dispositivo nuevo e ingrese el código de esa app. El factor actual sigue activo hasta que confirme.'
            : PROFILE_SECURITY_COPY.mfaSetupSubtitle
        }
        maxWidth="sm"
        actions={
          <FormHeaderSaveActions
            onCancel={closeMfaSetupModal}
            onPrimaryClick={handleConfirmMfa}
            primaryButtonType="button"
            className="profile-security-modal-actions"
            primaryLabel="Confirmar"
            showPrimaryIcon={false}
            isSaving={busy}
            primaryDisabled={busy || !canSubmitMfaVerification}
          />
        }
      >
        {setupData ? (
          <Stack spacing={1.5}>
            <GuidanceNote
              icon={<InfoOutlinedIcon />}
              title=""
              message={
                mfaReconfiguring
                  ? 'Abra la app en el equipo nuevo, escanee el código y escriba el número de 6 dígitos. La app anterior dejará de funcionar al confirmar.'
                  : PROFILE_SECURITY_COPY.mfaSetupGuidance
              }
              {...PROFILE_SECURITY_MODAL_GUIDANCE_PROPS}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  p: 1.25,
                  border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
                  borderRadius: '14px',
                  bgcolor: UI_COLOR_WHITE
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: UI_COLOR_TEXT_SUBTITLE,
                    mb: 1
                  }}
                >
                  {PROFILE_SECURITY_COPY.mfaSetupQrLabel}
                </Typography>
                <Box
                  component="img"
                  src={setupData.qr_code}
                  alt="Código QR para configurar la app autenticadora"
                  sx={{ width: { xs: 160, sm: 150 }, height: { xs: 160, sm: 150 }, display: 'block' }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <Stack spacing={1.25}>
                  <Typography sx={{ fontSize: 12.5, color: UI_COLOR_TEXT_SUBTITLE }}>
                    {PROFILE_SECURITY_COPY.mfaSetupCodeLabel}
                  </Typography>
                  <ProfileSecurityVerificationCodeInputs
                    digits={mfaVerificationDigits}
                    inputRefs={mfaCodeInputRefs}
                    onChange={handleMfaCodeChange}
                    onKeyDown={handleMfaCodeKeyDown}
                    onPaste={handleMfaCodePaste}
                    ariaLabelPrefix={
                      mfaReconfiguring ? 'Dígito del código de reconfiguración' : 'Dígito del código de activación'
                    }
                    idPrefix="mfa-setup-code"
                    focusColor={UI_COLOR_PRIMARY}
                  />
                </Stack>
              </Box>
            </Stack>
          </Stack>
        ) : null}
      </ProfileSecurityModalShell>

      <ProfileSecurityModalShell
        open={mfaReconfigureOpen}
        onClose={closeMfaReconfigureModal}
        title="Reconfigurar autenticación en dos pasos"
        subtitle="Confirme su identidad. Luego escaneará un QR en el dispositivo nuevo."
        actions={
          <FormHeaderSaveActions
            onCancel={closeMfaReconfigureModal}
            onPrimaryClick={handleReconfigureMfa}
            primaryButtonType="button"
            className="profile-security-modal-actions"
            primaryLabel="Continuar"
            showPrimaryIcon={false}
            isSaving={busy}
            primaryDisabled={busy || !reconfigureForm.currentPassword || !reconfigureForm.code.trim()}
          />
        }
      >
        <Stack spacing={1.75}>
          <GuidanceNote
            title=""
            message="Use el código de su app actual o un código de recuperación. El factor sigue activo hasta que confirme el código del dispositivo nuevo."
            {...PROFILE_SECURITY_MODAL_GUIDANCE_PROPS}
          />
          <ProfileSecurityStyledField
            label="Contraseña actual"
            type="password"
            value={reconfigureForm.currentPassword}
            onChange={(event) =>
              setReconfigureForm((current) => ({
                ...current,
                currentPassword: event.target.value
              }))
            }
            autoComplete="current-password"
          />
          <ProfileSecurityStyledField
            label="Código TOTP o de recuperación"
            value={reconfigureForm.code}
            onChange={(event) =>
              setReconfigureForm((current) => ({ ...current, code: event.target.value }))
            }
          />
        </Stack>
      </ProfileSecurityModalShell>

      <ProfileSecurityModalShell
        open={mfaDisableOpen}
        onClose={closeMfaDisableModal}
        title="Desactivar app autenticadora"
        subtitle="El código por correo seguirá siendo su método de verificación."
        actions={
          <FormHeaderSaveActions
            onCancel={closeMfaDisableModal}
            onPrimaryClick={handleDisableMfa}
            primaryButtonType="button"
            className="profile-security-modal-actions"
            primaryLabel="Desactivar"
            showPrimaryIcon={false}
            isSaving={busy}
            primaryDisabled={busy || !disableForm.currentPassword || !disableForm.code.trim()}
          />
        }
      >
        <Stack spacing={1.75}>
          <GuidanceNote
            title=""
            message="Confirme con su contraseña y el código de la app o uno de recuperación."
            {...PROFILE_SECURITY_WARNING_NOTE_PROPS}
          />
          <ProfileSecurityStyledField
            label="Contraseña actual"
            type="password"
            value={disableForm.currentPassword}
            onChange={(event) =>
              setDisableForm((current) => ({
                ...current,
                currentPassword: event.target.value
              }))
            }
            autoComplete="current-password"
          />
          <ProfileSecurityStyledField
            label="Código TOTP o de recuperación"
            value={disableForm.code}
            onChange={(event) =>
              setDisableForm((current) => ({ ...current, code: event.target.value }))
            }
          />
        </Stack>
      </ProfileSecurityModalShell>

      <ProfileSecurityModalShell
        open={regenerateOpen}
        onClose={closeRegenerateModal}
        title="Regenerar códigos de recuperación"
        subtitle="Los códigos anteriores dejarán de funcionar de inmediato."
        actions={
          <FormHeaderSaveActions
            onCancel={closeRegenerateModal}
            onPrimaryClick={handleRegenerateBackupCodes}
            primaryButtonType="button"
            className="profile-security-modal-actions"
            primaryLabel="Regenerar"
            showPrimaryIcon={false}
            isSaving={busy}
            primaryDisabled={
              busy || !regenerateForm.currentPassword || !regenerateForm.code.trim()
            }
          />
        }
      >
        <Stack spacing={1.75}>
          <GuidanceNote
            title=""
            message="Confirme su identidad. Luego verá 10 códigos nuevos una sola vez: cópielos o descárguelos."
            {...PROFILE_SECURITY_WARNING_NOTE_PROPS}
          />
          <ProfileSecurityStyledField
            label="Contraseña actual"
            type="password"
            value={regenerateForm.currentPassword}
            onChange={(event) =>
              setRegenerateForm((current) => ({
                ...current,
                currentPassword: event.target.value
              }))
            }
            autoComplete="current-password"
          />
          <ProfileSecurityStyledField
            label="Código TOTP o de recuperación"
            value={regenerateForm.code}
            onChange={(event) =>
              setRegenerateForm((current) => ({ ...current, code: event.target.value }))
            }
          />
        </Stack>
      </ProfileSecurityModalShell>

      <ProfileSecurityModalShell
        open={backupOpen}
        onClose={closeBackupModal}
        title="Códigos de recuperación"
        subtitle="Guárdelos ahora. Solo se muestran una vez; si cierra esta pantalla o los pierde, tendrá que regenerar otro set."
        actions={
          <FormHeaderDetailActions
            secondaryLabel="Descargar TXT"
            secondaryIcon={DownloadSimple}
            secondaryVariant="secondary"
            onSecondary={handleDownloadBackupCodes}
            secondaryDisabled={!backupCodes.length}
            primaryLabel="Copiar y terminar"
            primaryIcon={CopySimple}
            onPrimary={handleCopyBackupCodes}
            primaryDisabled={!backupCodes.length}
          />
        }
      >
        {backupCodes.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1
            }}
          >
            {backupCodes.map((code) => (
              <Box
                key={code}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1.15,
                  border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
                  borderRadius: '12px',
                  bgcolor: '#FAFBFC',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 13,
                  fontWeight: 700,
                  color: PROFILE_SECURITY_HEADING_COLOR
                }}
              >
                <KeyRoundedIcon sx={{ fontSize: 16, color: PROFILE_SECURITY_BACKUP_CODE_ICON_COLOR }} />
                {code}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13.5, color: UI_COLOR_TEXT_SUBTITLE }}>
            {PROFILE_SECURITY_COPY.backupCodesEmpty}
          </Typography>
        )}

        {backupCodes.length > 0 ? (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: UI_COLOR_TEXT_MUTED_LIGHT
            }}
          >
              <ContentCopyRoundedIcon sx={{ fontSize: 16, color: PROFILE_SECURITY_BACKUP_HINT_ICON_COLOR }} />
              <Typography sx={{ fontSize: 12.5 }}>
                Descárguelos o use «Copiar y terminar» para guardar este set antes de cerrar.
              </Typography>
            </Box>
          ) : null}
      </ProfileSecurityModalShell>
    </Box>
  )
}

